import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';

import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useDuplicateFolder } from '@utils/api/folders/use-duplicate-folder.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { WorkspaceRow } from '@components/main-workspace/workspace.tsx';

type Props = {
  selectedItem: WorkspaceRow;
  isOpen: boolean;
  onClose: () => void;
};

export const DuplicateFolderModal = ({
  selectedItem,
  isOpen,
  onClose
}: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const duplicateMutation = useDuplicateFolder();

  const defaultName = `${t('copy-of')} ${selectedItem.name ?? ''}`;
  const [name, setName] = useState(defaultName);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleClose = () => {
    setName(defaultName);
    setFieldError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setFieldError(t('field-required'));
      return;
    }
    setFieldError(null);

    try {
      const result = await duplicateMutation.mutateAsync({
        folder_id: Number(selectedItem.id),
        name: name.trim(),
        parent_id: selectedItem.parent_id ?? null
      });
      toast.success(
        t('folder-duplicated-successfully', {
          count: result.result.items_copied
        })
      );
      handleClose();
    } catch (error) {
      const err = error as AxiosError<BaseError>;
      const status = err?.response?.status;
      if (status === 409) {
        setFieldError(t('folder-name-already-exists'));
        return;
      }
      const data = err?.response?.data;
      const message =
        data?.message ??
        data?.error_description ??
        t('an-error-occurred-while-duplicating-the-folder');
      toast.error(message);
    }
  };

  const FORM_ID = 'duplicate-folder-form';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="small"
      title={`${t('duplicate-folder')} — ${selectedItem.name}`}
      footerActions={[
        <Button
          key="cancel"
          type="button"
          onClick={handleClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="duplicate"
          type="submit"
          form={FORM_ID}
          label={
            duplicateMutation.isLoading ? t('duplicating') : t('duplicate')
          }
          size="small"
          variant="primary"
          loading={duplicateMutation.isLoading}
        />
      ]}
    >
      <form
        id={FORM_ID}
        className="py-2"
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <button type="submit" className="hidden" aria-hidden="true" />

        <label
          htmlFor="duplicate-folder-name"
          className="mb-1.5 block text-xs font-semibold uppercase tracking-wide"
          style={{ color: getColour('inactiveText') }}
        >
          {t('name')}
        </label>
        <Input
          id="duplicate-folder-name"
          value={name}
          onChange={e => {
            setName(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          type="text"
          autoComplete="off"
          className="block w-full"
          autoFocus
        />
        {fieldError && (
          <p className="mt-1.5 text-xs" style={{ color: 'rgb(239, 68, 68)' }}>
            {fieldError}
          </p>
        )}
      </form>
    </Modal>
  );
};
